import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, Cell,
} from 'recharts';
import {
  Clock, ChevronLeft, ChevronRight, Wallet, X, Users,
} from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

type ZoomLevel = 'month' | 'week' | 'day' | 'hour';

interface ActivityItem {
  id: string;
  type: string;
  worldId: string | null;
  amount: string;
  currency: string;
  direction: 'credit' | 'debit';
  timestamp: string;
  metadata: Record<string, unknown> | null;
  debitAccount: string | null;
  creditAccount: string | null;
}

interface Holder {
  id: string;
  name: string;
  email: string;
  worldIds: string[];
  isActive: boolean;
}

interface TimeBucket {
  bucketStart: Date;
  bucketEnd: Date;
  label: string;
  credits: number;
  debits: number;
  items: ActivityItem[];
}

// ─── Zoom configuration ──────────────────────────────────────────────────────

type ZoomConfig = {
  windowMs: number;   // total visible window width
  bucketMs: number;   // width of each bar
  bucketCount: number;
  stepMs: number;     // how much prev/next moves
  label: string;
  zoomsIn: ZoomLevel | null;
  zoomsOut: ZoomLevel | null;
  bucketFmt: (d: Date) => string;
  xInterval: number;  // recharts tick interval
};

const ZOOM_CONFIGS: Record<ZoomLevel, ZoomConfig> = {
  month: {
    windowMs: 30 * 24 * 3_600_000,
    bucketMs:      24 * 3_600_000,
    bucketCount: 30,
    stepMs: 30 * 24 * 3_600_000,
    label: '30 Days',
    zoomsIn: 'week',
    zoomsOut: null,
    bucketFmt: (d) => format(d, 'MMM d'),
    xInterval: 4,
  },
  week: {
    windowMs: 7 * 24 * 3_600_000,
    bucketMs:     24 * 3_600_000,
    bucketCount: 7,
    stepMs: 7 * 24 * 3_600_000,
    label: '7 Days',
    zoomsIn: 'day',
    zoomsOut: 'month',
    bucketFmt: (d) => format(d, 'EEE d'),
    xInterval: 0,
  },
  day: {
    windowMs: 24 * 3_600_000,
    bucketMs:      3_600_000,
    bucketCount: 24,
    stepMs: 24 * 3_600_000,
    label: '1 Day',
    zoomsIn: 'hour',
    zoomsOut: 'week',
    bucketFmt: (d) => format(d, 'HH:mm'),
    xInterval: 3,
  },
  hour: {
    windowMs: 3_600_000,
    bucketMs:    60_000,
    bucketCount: 60,
    stepMs: 3_600_000,
    label: '1 Hour',
    zoomsIn: null,
    zoomsOut: 'day',
    bucketFmt: (d) => format(d, 'HH:mm'),
    xInterval: 9,
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TXN_LABELS: Record<string, string> = {
  HOLDER_FUNDING:        'World Funding',
  HOLDER_SEEDING:        'CB Seeded',
  WORLD_JOIN:            'World Join Bonus',
  TIP:                   'Tip / Gift',
  HOLDER_WORLD_EXCHANGE: 'World Exchange',
  PARTNER_FUND_HOLDER:   'Partner Funding',
  GIFT:                  'Gift',
  PLAYER_REWARD:         'Game Reward',
  EXCHANGE:              'UKC Exchange',
  HOLDER_WORLD_CONVERT:  'World ↔ UKC Convert',
};

function fmtAmt(amount: string) {
  const n = parseFloat(amount);
  if (isNaN(n)) return '0';
  // Trim trailing zeros while keeping meaningful decimal precision
  return n.toFixed(8).replace(/\.?0+$/, '') || '0';
}

/** Derive a human-readable source label from an activity item */
function activitySource(item: ActivityItem): string {
  const meta = item.metadata as Record<string, unknown> | null;
  if (item.type === 'TIP') {
    const reason = meta?.reason as string | undefined;
    return reason ? `Tip: "${reason}"` : 'Tip / Gift';
  }
  if (item.type === 'HOLDER_SEEDING') return 'Central Bank Seeded';
  if (item.type === 'HOLDER_FUNDING' || item.type === 'PARTNER_FUND_HOLDER') {
    const src = meta?.sourceAccount as string | undefined;
    return src ? `World Fund (${src})` : 'World Funded';
  }
  if (item.type === 'WORLD_JOIN') return 'World Join Bonus';
  if (item.type === 'PLAYER_REWARD') {
    const ruleName = meta?.ruleName as string | undefined;
    return ruleName ? `Rule: ${ruleName}` : 'Game Reward';
  }
  if (item.type === 'HOLDER_WORLD_EXCHANGE') return 'World-to-World Exchange';
  if (item.type === 'HOLDER_WORLD_CONVERT') return 'UKC ↔ World Conversion';
  return TXN_LABELS[item.type] ?? item.type;
}

const TOOLTIP_CTN_STYLE: React.CSSProperties = {
  background: '#0d1526',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '10px',
  fontSize: '11px',
  color: '#e2e8f0',
};

// ─── Component ───────────────────────────────────────────────────────────────

export function HolderTimelinePage() {
  const [selectedHolderId, setSelectedHolderId] = useState<string>('');
  const [zoom, setZoom]             = useState<ZoomLevel>('month');
  const [windowStart, setWindowStart] = useState<Date>(() => {
    return new Date(Date.now() - ZOOM_CONFIGS.month.windowMs);
  });
  const [selectedBucketIdx, setSelectedBucketIdx] = useState<number | null>(null);

  const cfg       = ZOOM_CONFIGS[zoom];
  const windowEnd = new Date(windowStart.getTime() + cfg.windowMs);

  // ── Fetch holder list ──────────────────────────────────────────────────────
  const { data: holders = [] } = useQuery<Holder[]>({
    queryKey: ['holders'],
    queryFn: () => api.get('/holders').then((r) => r.data),
  });

  // ── Fetch ALL activity for the holder (no date filter → enables full replay)
  const { data: rawActivity = [], isLoading: activityLoading } = useQuery<ActivityItem[]>({
    queryKey: ['holder-activity', selectedHolderId],
    queryFn:  () => api.get(`/holders/${selectedHolderId}/activity`).then((r) => r.data),
    enabled:  !!selectedHolderId,
    staleTime: 30_000,
  });

  // ── Build time buckets for the chart ──────────────────────────────────────
  const buckets = useMemo<TimeBucket[]>(() => {
    return Array.from({ length: cfg.bucketCount }, (_, i) => {
      const bucketStart = new Date(windowStart.getTime() + i * cfg.bucketMs);
      const bucketEnd   = new Date(bucketStart.getTime() + cfg.bucketMs);
      const bStart = bucketStart.getTime();
      const bEnd   = bucketEnd.getTime();

      const items = rawActivity.filter((a) => {
        const ts = new Date(a.timestamp).getTime();
        return ts >= bStart && ts < bEnd;
      });

      let credits = 0;
      let debits  = 0;
      for (const item of items) {
        const amt = parseFloat(item.amount);
        if (item.direction === 'credit') credits += amt;
        else                             debits  += amt;
      }

      return { bucketStart, bucketEnd, label: cfg.bucketFmt(bucketStart), credits, debits, items };
    });
  }, [rawActivity, windowStart, cfg]);

  // ── Point-in-time balance replay up to end of selected bucket ─────────────
  const pointInTimeBalances = useMemo<{ currency: string; net: number }[]>(() => {
    const asOf = selectedBucketIdx !== null
      ? (buckets[selectedBucketIdx]?.bucketEnd ?? new Date())
      : new Date();

    const totals: Record<string, number> = {};
    for (const item of rawActivity) {
      if (new Date(item.timestamp) > asOf) break; // rawActivity is ordered asc
      const amt = parseFloat(item.amount);
      totals[item.currency] = (totals[item.currency] ?? 0) + (item.direction === 'credit' ? amt : -amt);
    }

    return Object.entries(totals)
      .map(([currency, net]) => ({ currency, net }))
      .sort((a, b) => b.net - a.net);
  }, [rawActivity, selectedBucketIdx, buckets]);

  // ── Window navigation ─────────────────────────────────────────────────────
  const goBack    = useCallback(() => setWindowStart((d) => new Date(d.getTime() - cfg.stepMs)), [cfg.stepMs]);
  const goForward = useCallback(() => setWindowStart((d) => new Date(d.getTime() + cfg.stepMs)), [cfg.stepMs]);
  const goNow     = useCallback(() => {
    setWindowStart(new Date(Date.now() - cfg.windowMs));
    setSelectedBucketIdx(null);
  }, [cfg.windowMs]);

  const switchZoom = useCallback((newZoom: ZoomLevel) => {
    const centre = windowStart.getTime() + cfg.windowMs / 2;
    const newCfg = ZOOM_CONFIGS[newZoom];
    setWindowStart(new Date(centre - newCfg.windowMs / 2));
    setZoom(newZoom);
    setSelectedBucketIdx(null);
  }, [windowStart, cfg.windowMs]);

  const selectedBucket = selectedBucketIdx !== null ? buckets[selectedBucketIdx] : null;

  const hasAnyActivity = rawActivity.length > 0;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-5 min-h-full">

      {/* Page header */}
      <div>
        <h1 className="text-xl font-semibold text-white">Holder Timeline</h1>
        <p className="text-xs text-slate-500 mt-0.5">
          Transaction history with point-in-time balance replay
        </p>
      </div>

      {/* Holder selector card */}
      <div className="glass-card p-4">
        <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-2">
          Select Holder
        </label>
        <select
          value={selectedHolderId}
          onChange={(e) => {
            setSelectedHolderId(e.target.value);
            setSelectedBucketIdx(null);
          }}
          className="w-full max-w-sm bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/40 transition-colors cursor-pointer"
        >
          <option value="">— choose a holder —</option>
          {holders.map((h) => (
            <option key={h.id} value={h.id}>
              {h.name} ({h.email}){!h.isActive ? ' [inactive]' : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Empty state */}
      {!selectedHolderId && (
        <div className="glass-card p-12 flex flex-col items-center gap-3 text-center">
          <Users className="w-10 h-10 text-slate-700" />
          <p className="text-slate-500 text-sm">Select a holder to view their transaction timeline.</p>
        </div>
      )}

      {/* Timeline content */}
      {selectedHolderId && (
        <motion.div
          key={selectedHolderId}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 340, damping: 30 }}
          className="space-y-4"
        >
          {/* Controls bar */}
          <div className="glass-card p-3 flex flex-wrap items-center gap-3">

            {/* Zoom level selector */}
            <div className="flex items-center gap-px bg-white/[0.03] rounded-lg p-1 border border-white/[0.04]">
              {(['month', 'week', 'day', 'hour'] as ZoomLevel[]).map((z) => (
                <button
                  key={z}
                  onClick={() => switchZoom(z)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                    zoom === z
                      ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                      : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {ZOOM_CONFIGS[z].label}
                </button>
              ))}
            </div>

            {/* Prev / window label / Next */}
            <div className="flex items-center gap-1">
              <button
                onClick={goBack}
                className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-all"
                title="Previous period"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-slate-400 font-mono min-w-[220px] text-center">
                {format(windowStart, zoom === 'hour' ? 'MMM d, HH:mm' : 'MMM d, yyyy')}
                {' – '}
                {format(windowEnd, zoom === 'hour' ? 'HH:mm' : zoom === 'day' ? 'MMM d, yyyy' : 'MMM d, yyyy')}
              </span>
              <button
                onClick={goForward}
                className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-all"
                title="Next period"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={goNow}
              className="px-3 py-1.5 rounded-lg text-xs text-slate-400 hover:text-white border border-white/5 hover:bg-white/5 transition-all"
            >
              Today
            </button>

            {activityLoading && (
              <span className="text-[10px] text-slate-600 animate-pulse ml-2">Loading…</span>
            )}

            {selectedBucketIdx !== null && (
              <button
                onClick={() => setSelectedBucketIdx(null)}
                className="ml-auto flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-slate-400 hover:text-rose-400 border border-white/5 hover:border-rose-500/20 transition-all"
              >
                <X className="w-3 h-3" /> Clear selection
              </button>
            )}
          </div>

          {/* Chart card */}
          <div className="glass-card p-4">
            {/* Legend */}
            <div className="flex items-center gap-5 mb-4">
              <div className="flex items-center gap-1.5 text-xs text-emerald-400">
                <div className="w-3 h-3 rounded-sm bg-emerald-500/60" />
                Credits
              </div>
              <div className="flex items-center gap-1.5 text-xs text-rose-400">
                <div className="w-3 h-3 rounded-sm bg-rose-500/60" />
                Debits
              </div>
              {hasAnyActivity && (
                <span className="ml-auto text-[10px] text-slate-600">
                  {rawActivity.length.toLocaleString()} total transactions
                </span>
              )}
            </div>

            {!hasAnyActivity && !activityLoading ? (
              <div className="h-52 flex items-center justify-center text-slate-600 text-sm">
                No transactions found for this holder yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={230}>
                <BarChart
                  data={buckets}
                  barGap={1}
                  barCategoryGap="20%"
                  onClick={(e) => {
                    if (e?.activeTooltipIndex !== undefined) {
                      const idx = e.activeTooltipIndex;
                      setSelectedBucketIdx(idx === selectedBucketIdx ? null : (idx ?? null));
                    }
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.04)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: '#475569', fontSize: 10 }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255,255,255,0.06)' }}
                    interval={cfg.xInterval}
                  />
                  <YAxis
                    tick={{ fill: '#475569', fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={50}
                    tickFormatter={(v: number) => {
                      if (v === 0) return '0';
                      if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}k`;
                      return v.toFixed(v % 1 === 0 ? 0 : 2);
                    }}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                    contentStyle={TOOLTIP_CTN_STYLE}
                    formatter={(value: number, name: string) => [
                      value === 0 ? '—' : value.toFixed(4).replace(/\.?0+$/, ''),
                      name === 'credits' ? '+ Credits' : '− Debits',
                    ]}
                    labelFormatter={(label: string) => {
                      const bucket = buckets.find((b) => b.label === label);
                      const cnt = bucket?.items.length ?? 0;
                      return cnt > 0
                        ? `${label}  (${cnt} transaction${cnt !== 1 ? 's' : ''})`
                        : `${label}  (no activity)`;
                    }}
                  />
                  <ReferenceLine y={0} stroke="rgba(255,255,255,0.08)" />

                  <Bar dataKey="credits" name="credits" maxBarSize={28} radius={[3, 3, 0, 0]}>
                    {buckets.map((b, i) => (
                      <Cell
                        key={i}
                        fill={
                          i === selectedBucketIdx
                            ? 'rgba(52,211,153,0.95)'
                            : b.credits > 0
                              ? 'rgba(52,211,153,0.55)'
                              : 'rgba(52,211,153,0.07)'
                        }
                        stroke={i === selectedBucketIdx ? 'rgba(52,211,153,0.6)' : 'none'}
                        strokeWidth={1}
                      />
                    ))}
                  </Bar>

                  <Bar dataKey="debits" name="debits" maxBarSize={28} radius={[3, 3, 0, 0]}>
                    {buckets.map((b, i) => (
                      <Cell
                        key={i}
                        fill={
                          i === selectedBucketIdx
                            ? 'rgba(251,113,133,0.95)'
                            : b.debits > 0
                              ? 'rgba(251,113,133,0.55)'
                              : 'rgba(251,113,133,0.07)'
                        }
                        stroke={i === selectedBucketIdx ? 'rgba(251,113,133,0.6)' : 'none'}
                        strokeWidth={1}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Lower row: transaction detail + balance replay */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Transaction detail for selected bucket */}
            <div className="glass-card p-4">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 shrink-0" />
                {selectedBucket
                  ? `${format(selectedBucket.bucketStart, 'MMM d, HH:mm')} – ${format(selectedBucket.bucketEnd, 'HH:mm')}`
                  : 'Transactions'}
              </h3>

              <AnimatePresence mode="wait">
                {selectedBucket ? (
                  <motion.div
                    key={String(selectedBucketIdx)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                  >
                    {selectedBucket.items.length === 0 ? (
                      <p className="text-slate-600 text-xs text-center py-6">
                        No transactions in this period.
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-80 overflow-y-auto pr-1 scrollbar-thin">
                        {selectedBucket.items.map((item) => {
                          const isCredit = item.direction === 'credit';
                          return (
                            <div
                              key={item.id}
                              className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${
                                isCredit
                                  ? 'bg-emerald-500/5 border-emerald-500/15'
                                  : 'bg-rose-500/5 border-rose-500/15'
                              }`}
                            >
                              {/* Direction indicator */}
                              <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${
                                isCredit
                                  ? 'bg-emerald-500/20 text-emerald-400'
                                  : 'bg-rose-500/20 text-rose-400'
                              }`}>
                                {isCredit ? '+' : '−'}
                              </div>

                              <div className="flex-1 min-w-0">
                                {/* Source label + amount */}
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-xs font-medium text-white truncate">
                                    {activitySource(item)}
                                  </span>
                                  <span className={`text-xs font-mono font-semibold shrink-0 ${
                                    isCredit ? 'text-emerald-400' : 'text-rose-400'
                                  }`}>
                                    {isCredit ? '+' : '−'}{fmtAmt(item.amount)} {item.currency}
                                  </span>
                                </div>

                                {/* Type label + timestamp */}
                                <div className="flex items-center gap-3 mt-0.5">
                                  <span className="text-[10px] text-slate-600">
                                    {TXN_LABELS[item.type] ?? item.type}
                                  </span>
                                  <span className="text-[10px] text-slate-700">
                                    {format(new Date(item.timestamp), 'HH:mm:ss')}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </motion.div>
                ) : (
                  <motion.p
                    key="hint"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-slate-600 text-xs text-center py-6"
                  >
                    Click any bar on the chart to inspect transactions in that period.
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            {/* Point-in-time balance table */}
            <div className="glass-card p-4">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-2">
                <Wallet className="w-3.5 h-3.5 shrink-0" />
                Balance
              </h3>
              <p className="text-[10px] text-slate-600 mb-3">
                {selectedBucket
                  ? `Computed at end of ${format(selectedBucket.bucketEnd, 'MMM d HH:mm')} — replays all transactions up to that moment`
                  : 'Current balance — computed from full transaction history'}
              </p>

              {pointInTimeBalances.length === 0 ? (
                <p className="text-slate-600 text-xs text-center py-6">
                  No balance data available.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {pointInTimeBalances.map(({ currency, net }) => {
                    const display = net.toFixed(8).replace(/\.?0+$/, '') || '0';
                    return (
                      <div
                        key={currency}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                          net < 0
                            ? 'bg-rose-500/5 border-rose-500/10'
                            : net === 0
                              ? 'bg-white/[0.015] border-white/[0.04]'
                              : 'bg-white/[0.025] border-white/[0.06]'
                        }`}
                      >
                        <span className="text-xs font-medium text-slate-300">{currency}</span>
                        <span className={`text-xs font-mono font-semibold ${
                          net < 0 ? 'text-rose-400' : net === 0 ? 'text-slate-600' : 'text-white'
                        }`}>
                          {display}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </motion.div>
      )}
    </div>
  );
}
